import { ActionIcon, Card, FileInput, Group, Stack, TextInput, Title } from "@mantine/core";
import type { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Source } from "../NewTileset";
import { IconPlus, IconTrash } from "@tabler/icons-react";

export interface SourcesInputProps {
  isLoading: boolean;
  sources: Source[];
  onAddSource: () => void;
  getInputProps: (name: string) => object;
  onRemoveSource: (index: number) => void;
}

export const SourcesInput: FC<SourcesInputProps> = ({ isLoading, sources, getInputProps, onRemoveSource, onAddSource }) => {
  const intl = useIntl();

  const nameLabel = intl.formatMessage({
    defaultMessage: "Name",
    id: "label.name",
  });
  const fileLabel = intl.formatMessage({
    defaultMessage: "File",
    id: "label.file",
  });
  const sourceDescriptionLabel = intl.formatMessage({
    defaultMessage: "Combined tileset sources must be less than 10GB",
    id: "label.tilesetSourceDescriptionLimit",
  });

  const last = sources.length - 1;

  return (
    <Stack gap="xs">
      <Title order={3}>
        <FormattedMessage defaultMessage="Sources" id="label.tilesetSource" />
      </Title>
      {sourceDescriptionLabel}

      {sources.map((source, index) => (
        <Card key={source.id} withBorder shadow="sm" radius="md" h="120px">
          <Group gap="xs" align="flex-start" h="100%">
            <TextInput
              flex={1}
              label={nameLabel}
              disabled={isLoading}
              placeholder={nameLabel}
              {...getInputProps(`sources.${index}.name`)}
            />

            <FileInput
              flex={1}
              label={fileLabel}
              disabled={isLoading}
              placeholder={fileLabel}
              {...getInputProps(`sources.${index}.file`)}
            />

            <Group my="auto" gap="xs" align="center">
              {last !== 0 && (
                <ActionIcon color="red.8" onClick={() => { onRemoveSource(index); }}>
                  <IconTrash size="1rem" />
                </ActionIcon>
              )}

              {index === last && (
                <ActionIcon color="green.8" onClick={() => { onAddSource(); }}>
                  <IconPlus size="1rem" />
                </ActionIcon>
              )}
            </Group>
          </Group>
        </Card>
      ))}
    </Stack>
  );
};
